import type {ReactNode} from 'react';

import {
  AfriexAdminPermission,
  BusinessFeeNegotiationStatus,
} from '@afriex/core/domain/enums';
import type {BusinessFeeNegotiation} from '@afriex/core/domain/types';
import {getNegotiationDisplay} from '@afriex/core/domain/types';
import type {NegotiationLineChange} from '@afriex/core/utils';
import {formatFeeAmount, getCorridorKey} from '@afriex/core/utils';

import {Button, PermissionsProvider, TextArea} from '@/components';

import type {CorridorColumn} from '../Fee/CorridorLineTable';
import CorridorLineTable from '../Fee/CorridorLineTable';
import CounterRateInput from './CounterRateInput';
import {
  LineChangeBadge,
  LineOutcomeBadge,
  NegotiationStatusBadge,
} from './NegotiationBadges';
import {
  formatCorridorLabel,
  formatDay,
  formatDayTime,
  formatMonthlyVolume,
  getRoundLabel,
  isSalesCallWithdrawal,
} from './negotiationFormat';
import type {PriceChangeRow} from './NegotiationModals';
import {
  AcceptPricingModal,
  ApplyPricingModal,
  DeclinePricingModal,
  SendCounterModal,
} from './NegotiationModals';
import NegotiationRoundsTable from './NegotiationRoundsTable';
import {useNegotiationAnswer} from './useNegotiationAnswer';

const EMPTY = '-';
const BUTTON_SIZE = 'text-xs px-3 py-1 h-8';

const SummaryRow = ({label, value}: {label: string; value: ReactNode}) => (
  <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
    <p className="flex-[0.4] text-slate-500 text-sm dark:text-slate-100">
      {label}
    </p>
    <div className="flex-[0.6] text-sm font-medium text-gray-700 dark:text-gray-100">
      {value ?? EMPTY}
    </div>
  </div>
);

const Section = ({title, children}: {title: string; children: ReactNode}) => (
  <div className="mt-6">
    <h4 className="font-semibold text-gray-900 dark:text-gray-300 mb-2">
      {title.toLocaleUpperCase()}
    </h4>
    <hr className="mb-3 dark:border-dark-borderMid" />
    {children}
  </div>
);

/** The merchant's ask, with the price it moved from struck through. */
const AskCell = ({line}: {line: NegotiationLineChange}) => (
  <span>
    {line.previousAmount && line.isChanged ? (
      <span className="mr-1 text-gray-400 line-through dark:text-gray-500">
        {formatFeeAmount(line.previousAmount)}
      </span>
    ) : null}
    <span className="font-medium">{formatFeeAmount(line.proposedAmount)}</span>
  </span>
);

const buildColumns = (
  negotiation: BusinessFeeNegotiation,
  isOpen: boolean,
  isCounter: boolean,
  renderInput: (line: NegotiationLineChange) => ReactNode,
): CorridorColumn<NegotiationLineChange>[] => {
  const volume: CorridorColumn<NegotiationLineChange> = {
    key: 'volume',
    header: 'Monthly volume',
    render: (line) => formatMonthlyVolume(line.monthlyVolumeUsd),
  };
  const current: CorridorColumn<NegotiationLineChange> = {
    key: 'current',
    header: 'Current',
    render: (line) => formatFeeAmount(line.defaultAmount) || EMPTY,
  };

  if (!isOpen) {
    const isAccepted =
      negotiation.status === BusinessFeeNegotiationStatus.Accepted;
    return [
      volume,
      current,
      {
        key: 'agreed',
        header: isAccepted ? 'Agreed price' : 'Was asking',
        render: (line) => formatFeeAmount(line.proposedAmount),
      },
      {
        key: 'outcome',
        header: 'Outcome',
        render: () => (
          <LineOutcomeBadge
            isApplied={isAccepted && Boolean(negotiation.rulesAppliedAt)}
          />
        ),
      },
    ];
  }

  return [
    volume,
    current,
    {
      key: 'asks',
      header: 'Merchant asks',
      render: (line) => <AskCell line={line} />,
    },
    isCounter
      ? {key: 'yours', header: 'Your price', render: renderInput}
      : {
          key: 'change',
          header: 'Change',
          render: (line) => (
            <LineChangeBadge
              isChanged={line.isChanged || line.isOpeningRound}
            />
          ),
        },
  ];
};

/** What closed the request, in words, for a settled negotiation. */
const getClosedNote = (negotiation: BusinessFeeNegotiation): string | null => {
  switch (negotiation.status) {
    case BusinessFeeNegotiationStatus.Rejected:
      return negotiation.rejectionNote
        ? `Declined: ${negotiation.rejectionNote}`
        : 'Declined.';
    case BusinessFeeNegotiationStatus.Cancelled:
      return isSalesCallWithdrawal(negotiation)
        ? 'The merchant withdrew to speak to Sales. No pricing changed.'
        : 'The merchant withdrew this request. No pricing changed.';
    default:
      return null;
  }
};

type NegotiationReviewProps = {
  negotiation: BusinessFeeNegotiation;
  onAnswered: () => void;
};

/**
 * One pricing request in full: who and when, the corridors, every round, and
 * Afriex's answer. Key it by negotiation so a new one starts from a clean form.
 */
const NegotiationReview = ({
  negotiation,
  onAnswered,
}: NegotiationReviewProps) => {
  const answer = useNegotiationAnswer(negotiation, onAnswered);
  const {state, dispatch, lines, isSubmitting, isFinalRound} = answer;
  const display = getNegotiationDisplay(negotiation);
  const name = negotiation.businessName ?? negotiation.businessId;
  const rounds = negotiation.rounds ?? [];
  const standingRound = rounds[rounds.length - 1];
  const openedBy = rounds[0]?.byName;
  const canAnswer =
    negotiation.status === BusinessFeeNegotiationStatus.PendingAdmin;
  const needsApply =
    negotiation.status === BusinessFeeNegotiationStatus.Accepted &&
    !negotiation.rulesAppliedAt;
  const isCounter = canAnswer && state.mode === 'counter';
  const changedCount = answer.validator.changedLines().length;
  const closedNote = getClosedNote(negotiation);

  const acceptRows: PriceChangeRow[] = lines.map((line) => ({
    key: getCorridorKey(line.corridor),
    corridor: formatCorridorLabel(line.corridor),
    from: formatFeeAmount(line.defaultAmount) || EMPTY,
    to: formatFeeAmount(line.proposedAmount),
  }));

  const getCounterRows = (): PriceChangeRow[] => {
    if (state.confirm !== 'counter') {
      return [];
    }
    try {
      const sent = new Map(
        answer.validator
          .value()
          .lines.map((line) => [
            getCorridorKey(line.corridor),
            line.proposedAmount,
          ]),
      );
      return answer.validator.changedLines().map((line) => {
        const key = getCorridorKey(line.corridor);
        return {
          key,
          corridor: formatCorridorLabel(line.corridor),
          from: formatFeeAmount(line.proposedAmount),
          to: formatFeeAmount(sent.get(key)),
        };
      });
    } catch {
      return [];
    }
  };

  const modalProps = {
    name,
    isLoading: isSubmitting,
    error: answer.error,
    onBack: () => dispatch({type: 'CANCEL_CONFIRM'}),
    onConfirm: answer.confirm,
  };

  const columns = buildColumns(
    negotiation,
    display.isOpen,
    isCounter,
    (line) => <CounterRateInput line={line} answer={answer} />,
  );

  const renderActions = () => {
    if (!canAnswer) {
      return null;
    }
    if (isCounter) {
      return (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          Editing a counter offer
        </span>
      );
    }
    return (
      <PermissionsProvider
        permission={AfriexAdminPermission.BusinessFeeNegotiationUpdate}
      >
        <div className="flex flex-wrap items-center justify-end gap-2">
          {isFinalRound ? (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Final round. Accept or decline the pricing.
            </span>
          ) : null}
          <Button
            variant="outline"
            colorScheme="red"
            className={BUTTON_SIZE}
            disabled={isSubmitting}
            onClick={() => dispatch({type: 'ASK_CONFIRM', confirm: 'decline'})}
          >
            Decline
          </Button>
          {isFinalRound ? null : (
            <Button
              variant="outline"
              colorScheme="cyan"
              className={BUTTON_SIZE}
              disabled={isSubmitting}
              onClick={() => dispatch({type: 'OPEN_COUNTER', lines})}
            >
              Adjust pricing
            </Button>
          )}
          <Button
            colorScheme="cyan"
            className={BUTTON_SIZE}
            disabled={isSubmitting}
            onClick={() => dispatch({type: 'ASK_CONFIRM', confirm: 'accept'})}
          >
            Accept pricing
          </Button>
        </div>
      </PermissionsProvider>
    );
  };

  return (
    <div className="bg-white dark:bg-transparent rounded-lg w-full min-w-0 px-5 py-5">
      <div className="flex flex-wrap items-center justify-between mb-3 gap-3">
        <div className="flex flex-wrap items-center gap-4">
          <h2 className="font-semibold text-xl mr-3">{name}</h2>
          <NegotiationStatusBadge negotiation={negotiation} />
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {getRoundLabel(negotiation)}
          </span>
        </div>
        {renderActions()}
      </div>

      {needsApply ? (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-300">
          <span>
            <b>Pricing not applied.</b>{' '}
            {`Both sides agreed, but the new pricing was not saved. ${name} is still on the default fees.`}
          </span>
          <PermissionsProvider
            permission={AfriexAdminPermission.BusinessFeeNegotiationUpdate}
          >
            <Button
              className={BUTTON_SIZE}
              disabled={isSubmitting}
              onClick={() => dispatch({type: 'ASK_CONFIRM', confirm: 'apply'})}
            >
              Apply pricing
            </Button>
          </PermissionsProvider>
        </div>
      ) : null}

      {answer.error && !state.confirm && !isCounter ? (
        <p className="mb-3 text-sm text-red-600 dark:text-red-400">
          {answer.error}
        </p>
      ) : null}

      <SummaryRow
        label="Merchant reference"
        value={negotiation.businessReference ?? negotiation.businessId}
      />
      <SummaryRow
        label="Opened"
        value={`${formatDay(negotiation.createdAt)}${openedBy ? ` by ${openedBy}` : ''}`}
      />
      <SummaryRow
        label="Revisit clause"
        value={
          negotiation.revisitClauseAcceptedAt
            ? `Accepted ${formatDayTime(negotiation.revisitClauseAcceptedAt)}`
            : 'Not accepted'
        }
      />
      {negotiation.acceptedAt ? (
        <SummaryRow
          label="Accepted"
          value={`${formatDayTime(negotiation.acceptedAt)}${negotiation.acceptedByName ? ` by ${negotiation.acceptedByName}` : ''}`}
        />
      ) : null}
      {negotiation.rulesAppliedAt ? (
        <SummaryRow
          label="Pricing applied"
          value={formatDayTime(negotiation.rulesAppliedAt)}
        />
      ) : null}

      {closedNote ? (
        <p className="mt-4 rounded-lg border-l-4 border-gray-300 dark:border-gray-600 bg-gray-50 px-4 py-3 text-sm text-gray-700 whitespace-normal break-words dark:bg-gray-900 dark:text-gray-200">
          {closedNote}
        </p>
      ) : null}
      {standingRound?.note ? (
        <p className="mt-4 rounded-lg border-l-4 border-cyan-500 dark:border-cyan-400 bg-cyan-50 px-4 py-3 text-sm text-gray-700 whitespace-normal break-words dark:bg-gray-900 dark:text-gray-200">
          &ldquo;{standingRound.note}&rdquo;{' '}
          <span className="text-gray-500 dark:text-gray-400">
            {`${standingRound.byName ?? 'Unknown'}, round ${rounds.length}`}
          </span>
        </p>
      ) : null}

      <Section title="Corridors">
        <CorridorLineTable
          rows={lines}
          getCorridor={(line) => line.corridor}
          columns={columns}
        />
        {isCounter ? (
          <div className="mt-4 flex flex-col gap-3">
            <TextArea
              label="Note for the merchant (optional)"
              name="pricingCounterNote"
              value={state.counterNote}
              onChange={(event) =>
                dispatch({type: 'SET_COUNTER_NOTE', note: event.target.value})
              }
              placeholder="Explain the counter offer"
            />
            {answer.error && !state.confirm ? (
              <p className="text-sm text-red-600 dark:text-red-400">
                {answer.error}
              </p>
            ) : null}
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                colorScheme="cyan"
                onClick={() => dispatch({type: 'CANCEL_COUNTER'})}
              >
                Cancel
              </Button>
              <Button onClick={answer.askToSendCounter}>
                {changedCount
                  ? `Review counter (${changedCount} ${changedCount === 1 ? 'change' : 'changes'})`
                  : 'Review counter'}
              </Button>
            </div>
          </div>
        ) : null}
      </Section>

      <Section title="Rounds">
        <NegotiationRoundsTable rounds={rounds} />
      </Section>

      {state.confirm === 'accept' ? (
        <AcceptPricingModal {...modalProps} rows={acceptRows} />
      ) : null}
      {state.confirm === 'counter' ? (
        <SendCounterModal {...modalProps} rows={getCounterRows()} />
      ) : null}
      {state.confirm === 'decline' ? (
        <DeclinePricingModal
          {...modalProps}
          reason={state.rejectionNote}
          onReasonChange={(note) =>
            dispatch({type: 'SET_REJECTION_NOTE', note})
          }
        />
      ) : null}
      {state.confirm === 'apply' ? (
        <ApplyPricingModal {...modalProps} count={lines.length} />
      ) : null}
    </div>
  );
};

export default NegotiationReview;
